import { Col } from "components/primitives";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";

import { AnalogClock } from "./AnalogClock";
import { DigitalClock } from "./DigitalClock";

export interface HandPositions {
  hours: number;
  minutes: number;
  seconds: number;
}

export interface InteractiveClockState {
  time?: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  handPositions: HandPositions;
}

function dateTimeToHandPositions(time: Date): HandPositions {
  return {
    hours: (time.getHours() / 12 + time.getMinutes() / 720) * 360,
    minutes: (time.getMinutes() / 60) * 360,
    seconds: (time.getSeconds() / 60) * 360,
  };
}

function handPositionsToDateTime(prevPositions: HandPositions, handPositions: HandPositions) {
  const dateTime = new Date();
  dateTime.setHours((handPositions.hours / 360) * 12);
  dateTime.setMinutes((handPositions.minutes / 360) * 60);
  dateTime.setSeconds((handPositions.seconds / 360) * 60);

  if (prevPositions.minutes > 45 && handPositions.minutes < 15) {
    dateTime.setHours(dateTime.getHours() + 1);
  } else if (prevPositions.minutes < 15 && handPositions.minutes > 45) {
    dateTime.setHours(dateTime.getHours() - 1);
  }
  if (prevPositions.seconds > 45 && handPositions.seconds < 15) {
    dateTime.setMinutes(dateTime.getMinutes() + 1);
  } else if (prevPositions.seconds < 15 && handPositions.seconds > 45) {
    dateTime.setMinutes(dateTime.getMinutes() - 1);
  }

  return dateTime;
}

export const InteractiveClock: WidgetFC<InteractiveClockState> = ({
  state: { time, handPositions },
  setState,
  settings: { is24Hour, clockFace, showSeconds, controlHands },
  pixelPosition,
}) => {
  const [shownTime, setShownTime] = useState<Date>(new Date());
  const [intervalPtr, setIntervalPtr] = useState<NodeJS.Timeout>();
  const is24HourBool = Boolean(is24Hour === "24 Hour");

  function setTime(newTime: Date, newHandPositions?: HandPositions) {
    setState({
      time: {
        hours: newTime.getHours(),
        minutes: newTime.getMinutes(),
        seconds: newTime.getSeconds(),
      },
      handPositions: newHandPositions ? newHandPositions! : dateTimeToHandPositions(newTime),
    });
  }

  useEffect(() => {
    if (time) {
      if (intervalPtr) {
        clearInterval(intervalPtr);
        setIntervalPtr(undefined);
      }

      setShownTime(new Date(0, 0, 0, time.hours, time.minutes, time.seconds));
      return;
    }

    if (!intervalPtr && !controlHands) {
      setIntervalPtr(
        setInterval(() => {
          setShownTime(new Date());
        }, 300),
      );
      setShownTime(new Date());
    }

    if (!time && controlHands) {
      setTime(new Date());
    }

    return () => clearInterval(intervalPtr);
  }, [time, intervalPtr]);

  useEffect(() => {
    if (controlHands) {
      setTime(shownTime);
    }
  }, [controlHands]);

  const setFreeState = (newPositions: HandPositions) => {
    setTime(handPositionsToDateTime(handPositions, newPositions), newPositions);
  };

  const setFixedTime = (newPositions: HandPositions) => {
    const newDate = handPositionsToDateTime(handPositions, newPositions);
    setTime(newDate);
  };

  return (
    <Container>
      {(clockFace === "a" || clockFace === "a_d") && (
        <AnalogClockContainer>
          <AnalogClock
            position={pixelPosition}
            handPositions={controlHands ? handPositions : dateTimeToHandPositions(shownTime)}
            setHandPositions={controlHands ? setFreeState : setFixedTime}
            showSeconds={showSeconds}
          />
        </AnalogClockContainer>
      )}
      {(clockFace === "d" || clockFace === "a_d") && (
        <DigitalClockContainer>
          <ClockBackground />
          <DigitalClock
            is24Hour={is24HourBool}
            position={pixelPosition}
            time={shownTime}
            setTime={setTime}
            showSeconds={showSeconds}
          />
        </DigitalClockContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 5cqw;
  display: flex;
  flex-wrap: wrap;
  gap: 5cqw;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const AnalogClockContainer = styled.div`
  width: 80cqw;
  height: 80cqw;
  container-type: size;
  position: relative;
`;

const DigitalClockContainer = styled(Col)`
  width: 100%;
  align-items: center;
  position: relative;
`;

const ClockBackground = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -40%);
  width: 85cqw;
  height: 10cqh;
  border: 1px solid var(--secondary);
  background-color: #eaf0ff;
  border-radius: 3px;
`;
